import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { login } from "../lib/api";
import { saveToken } from "../lib/auth";
import { PhoneLoginForm } from "../components/PhoneLoginForm";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { from?: { pathname?: string }; signedUpEmail?: string };
  };
  const dest = location.state?.from?.pathname || "/";
  // Set right after a successful invite acceptance — we prefill the
  // email and show a "your account is ready" banner so the new user
  // knows to sign in with the password they just set.
  const signedUpEmail = location.state?.signedUpEmail;

  // Two sign-in methods: email + password, or passwordless phone (OTP).
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState(signedUpEmail ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const resp = await login(email, password);
      saveToken(resp.token);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/agrilens-background.jpg')" }}
    >
      {/* Light scrim over the wheat-field background so the sign-in
          card keeps strong contrast on any monitor. The background is
          a clean texture (no baked-in logo), so the card carries the
          AgriLens.AI mark itself. */}
      <div className="absolute inset-0 bg-white/25" aria-hidden="true" />
      <div className="relative z-10 w-full max-w-sm bg-white/95 backdrop-blur-sm shadow-xl rounded-lg p-6 space-y-4">
        <div className="flex justify-center pb-2">
          <img
            src="/logo-app.png"
            alt="AgriLens.AI"
            className="h-24 w-auto"
          />
        </div>

        {/* Method switch: password vs passwordless phone (OTP). */}
        <div className="flex rounded-md border overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setMethod("email")}
            className={`flex-1 py-2 font-medium ${
              method === "email"
                ? "bg-agrilens-green text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setMethod("phone")}
            className={`flex-1 py-2 font-medium ${
              method === "phone"
                ? "bg-agrilens-green text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            Phone
          </button>
        </div>

        {method === "email" ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Sign in with your enterprise credentials.
            </p>

            {signedUpEmail && !error && (
              <div className="bg-green-50 text-green-800 text-sm p-2 rounded">
                Your account is ready. Sign in with the password you just
                set.
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 text-sm p-2 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-agrilens-green"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-agrilens-green"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-agrilens-green text-white font-medium py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-center pt-2">
              <Link
                to="/forgot"
                className="text-xs text-agrilens-green hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        ) : (
          <PhoneLoginForm dest={dest} />
        )}

        <p className="text-xs text-gray-500 text-center">
          Have an invite? Use the link in your email, or switch to Phone if
          you were invited by number.
        </p>
      </div>
    </div>
  );
}
